/**
 * Live and Draft changes side by side, for an integration customers already
 * use. The live version keeps serving while one working draft is edited,
 * tried by its testing organizations, reviewed by an approver and applied in place.
 */
import { AlertTriangle, CheckCircle2, FlaskConical, Loader2, Pencil, Rocket, Send, ShieldCheck, Trash2, Undo2, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { adminClient, type IntegrationDraft } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAdminAccess } from '@/hooks/useAdminAccess';

import { LIVE_EDIT_LABELS, errorMessage, liveEditActions, relativeTime, statusMetaFor, type LiveEditAction } from './builderModel';
import { TestingOrganizationsField, testingOrganizationsInput } from './TestingOrganizationsField';

const ICONS: Record<LiveEditAction, ReactNode> = {
  edit: <Pencil className="mr-2 h-4 w-4" />,
  withdraw: <Undo2 className="mr-2 h-4 w-4" />,
  publish_testing: <FlaskConical className="mr-2 h-4 w-4" />,
  stop_testing: <X className="mr-2 h-4 w-4" />,
  submit: <Send className="mr-2 h-4 w-4" />,
  approve: <ShieldCheck className="mr-2 h-4 w-4" />,
  reject: <X className="mr-2 h-4 w-4" />,
  apply: <Rocket className="mr-2 h-4 w-4" />,
  discard: <Trash2 className="mr-2 h-4 w-4" />,
};

const CONFIRMATIONS: Partial<Record<LiveEditAction, string>> = {
  stop_testing: 'Stop testing? Its testing organizations go back to the live version.',
  withdraw: 'Take the draft back to editing? It has to be submitted and approved again.',
  discard: 'Discard the draft changes? The live version stays exactly as it is, and values stored for the draft are deleted.',
};

const SUCCESS: Record<LiveEditAction, string> = {
  edit: 'Draft started. Customers keep the live version.',
  withdraw: 'Back to editing',
  publish_testing: 'Published for its testing organizations',
  stop_testing: 'Its testing organizations are back on the live version',
  submit: 'Sent for review',
  approve: 'Approved',
  reject: 'Sent back with your reason',
  apply: 'Changes applied for whoever can use it',
  discard: 'Draft discarded',
};

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 text-sm">
      <dt className="w-28 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 flex-1">{children}</dd>
    </div>
  );
}

function when(iso?: string): string {
  return iso ? ` · ${relativeTime(iso)}` : '';
}

export function LiveDraftPanel({ draft, replace }: { draft: IntegrationDraft; replace: (draft: IntegrationDraft) => void }) {
  const { canApproveIntegrations } = useAdminAccess();
  const [busy, setBusy] = useState<LiveEditAction>();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [reconnectConfirmed, setReconnectConfirmed] = useState(false);
  const [choosingTesting, setChoosingTesting] = useState(false);
  const [testingOrganizationIds, setTestingOrganizationIds] = useState<string[]>(() => draft.testingOrganizationIds ?? []);
  const live = draft.live;
  if (!draft.hasRelease || !live) return null;

  const actions = liveEditActions(draft, canApproveIntegrations);
  const changes = draft.changes ?? [];
  const connectionChanges = draft.connectionChanges ?? [];
  const pricingChanged = changes.some((change) => change.section === 'pricing');

  const run = async (action: LiveEditAction) => {
    if (action === 'reject' && !rejecting) {
      setRejecting(true);
      return;
    }
    if (action === 'publish_testing' && !choosingTesting) {
      setChoosingTesting(true);
      return;
    }
    if (action === 'apply') {
      const prompt = [
        'Apply the changes for whoever can use it? The live version is replaced in place, so existing apps and connections keep working with the new version.',
        pricingChanged ? 'The new pricing applies to every organization from now on.' : '',
      ]
        .filter(Boolean)
        .join(' ');
      if (!window.confirm(prompt)) return;
    } else if (CONFIRMATIONS[action] && !window.confirm(CONFIRMATIONS[action])) {
      return;
    }
    setBusy(action);
    try {
      const next =
        action === 'edit' || action === 'withdraw'
          ? await adminClient.setIntegrationDraftLifecycle(draft.draftId, 'reopen')
          : action === 'publish_testing'
            ? await adminClient.setIntegrationDraftTesting(draft.draftId, true, testingOrganizationsInput(testingOrganizationIds))
            : action === 'stop_testing'
              ? await adminClient.setIntegrationDraftTesting(draft.draftId, false)
              : action === 'submit'
                ? await adminClient.submitIntegrationDraft(draft.draftId)
                : action === 'approve' || action === 'reject'
                  ? await adminClient.reviewIntegrationDraft(draft.draftId, { decision: action === 'approve' ? 'approved' : 'rejected', note: action === 'reject' ? reason : '' })
                  : action === 'apply'
                    ? await adminClient.applyIntegrationDraft(draft.draftId, { confirmReconnect: connectionChanges.length > 0 && reconnectConfirmed })
                    : await adminClient.discardIntegrationDraft(draft.draftId);
      replace(next);
      setRejecting(false);
      setChoosingTesting(false);
      setReason('');
      setReconnectConfirmed(false);
      toast.success(SUCCESS[action]);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(undefined);
    }
  };

  const disabled = (action: LiveEditAction) =>
    Boolean(busy) ||
    (action === 'submit' && !draft.readyForReview) ||
    (action === 'publish_testing' && !draft.readyForReview) ||
    (action === 'apply' && connectionChanges.length > 0 && !reconnectConfirmed) ||
    (action === 'reject' && rejecting && !reason.trim());

  const meta = statusMetaFor(draft);

  return (
    <section aria-label="Live and draft changes" className="mb-6 grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-success/30 bg-success-bg/20 p-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="text-sm font-semibold">Live</div>
          <StatusPill tone="success">Customers use this</StatusPill>
        </div>
        <dl className="space-y-2">
          <Row label="Name">{live.title}</Row>
          <Row label="Pricing">{live.pricing}</Row>
          <Row label="Endpoints">{live.operationCount}</Row>
          <Row label="Approved by">{live.approvedBy ? `${live.approvedBy}${when(live.approvedAt)}` : 'Not recorded'}</Row>
          <Row label="Made live by">{live.releasedBy ? `${live.releasedBy}${when(live.releasedAt)}` : 'Not recorded'}</Row>
        </dl>
      </div>

      <div className="rounded-xl border border-border bg-card/40 p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm font-semibold">Draft changes</div>
          {draft.hasDraftChanges ? (
            <div className="flex flex-wrap items-center gap-2">
              {draft.testing ? <StatusPill tone="active">Testing organizations have it</StatusPill> : null}
              <StatusPill tone={meta.tone}>{meta.label.replace('Live · ', '')}</StatusPill>
            </div>
          ) : null}
        </div>

        {draft.hasDraftChanges ? (
          <div className="space-y-4">
            {draft.editStartedBy ? (
              <p className="text-xs text-muted-foreground">
                Started by {draft.editStartedBy}
                {when(draft.editStartedAt)}. Customers keep the live version until the changes are approved and applied.
              </p>
            ) : null}
            {changes.length ? (
              <ul className="space-y-1.5 text-sm">
                {changes.map((change) => (
                  <li key={`${change.section}:${change.text}`} className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span>{change.text}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No differences from the live version yet. Edit any step to change it.</p>
            )}
            {draft.status === 'rejected' && draft.review ? (
              <div className="rounded-lg border border-warning/30 bg-warning-bg/40 p-3 text-sm">
                <div className="font-medium">Rejected by {draft.review.reviewedBy}</div>
                <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{draft.review.note}</p>
              </div>
            ) : null}
            {connectionChanges.length ? (
              <div role="alert" className="rounded-lg border border-warning/40 bg-warning-bg/40 p-3 text-sm">
                <div className="flex items-center gap-2 font-medium">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  Existing customer connections may need to reconnect
                </div>
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
                  {connectionChanges.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                {actions.includes('apply') ? (
                  <label className="mt-3 flex items-start gap-2 text-xs">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={reconnectConfirmed}
                      onChange={(event) => setReconnectConfirmed(event.target.checked)}
                    />
                    I understand that organizations and people who already connected may have to connect again.
                  </label>
                ) : null}
              </div>
            ) : null}
            {choosingTesting ? (
              <div className="space-y-2 rounded-lg border border-border bg-background/60 p-3">
                <p className="text-xs text-muted-foreground">
                  The testing organizations use the draft in place of the live version everywhere in Studio: the catalog, pricing, new apps and
                  calls from existing apps. Everyone else keeps the live version.
                </p>
                <TestingOrganizationsField value={testingOrganizationIds} onChange={setTestingOrganizationIds} disabled={Boolean(busy)} />
              </div>
            ) : null}
            {rejecting ? (
              <Textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why, and what to change (the author sees this)" />
            ) : null}
            {draft.status === 'in_review' && !canApproveIntegrations ? (
              <p className="text-xs text-muted-foreground">An integration approver approves or rejects it.</p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No draft changes. Edit makes a working copy of the live version; customers keep the live version until your changes are approved and
            applied.
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {actions.map((action) => (
            <Button
              key={action}
              size="sm"
              variant={action === 'apply' || action === 'approve' || action === 'edit' || action === 'submit' ? 'default' : 'outline'}
              onClick={() => void run(action)}
              disabled={disabled(action)}
            >
              {busy === action ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : ICONS[action]}
              {action === 'reject' && rejecting ? 'Send back' : action === 'publish_testing' && choosingTesting ? 'Publish to testing organizations' : LIVE_EDIT_LABELS[action]}
            </Button>
          ))}
          {rejecting || choosingTesting ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setRejecting(false);
                setChoosingTesting(false);
              }}
              disabled={Boolean(busy)}
            >
              Cancel
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
